import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import axiosInstance from "../../../utils/axiosInstance";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const RoleFormModal = ({ open, onClose, roleId = null, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [permissionModules, setPermissionModules] = useState([]);
  const [permissionError, setPermissionError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      roleName: "",
      permissionIds: []
    }
  });

  const selectedIds = watch("permissionIds") || [];
  const hasSelectedPermissions = selectedIds.length > 0;

  const groupedPermissions = useMemo(() => {
    return permissionModules.reduce((acc, mod) => {
      acc[mod.module] = mod.permissions || [];
      return acc;
    }, {});
  }, [permissionModules]);

  // Load data when modal opens
  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      setInitialLoading(true);
      try {
        const [permRes, roleRes] = await Promise.all([
          axiosInstance.get("/api/admin/permissions"),
          roleId ? axiosInstance.get(`/api/admin/roles/${roleId}`) : null
        ]);

        const modules = permRes.data?.data?.records || [];
        setPermissionModules(modules);

        if (roleId && roleRes) {
          const role = roleRes.data?.data;

          const permissionIds = Array.isArray(role?.permissionIds)
            ? role.permissionIds
            : Array.isArray(role?.permissions)
              ? role.permissions.map(p => p.id)
              : [];

          const roleName =
            role?.roleName ||
            role?.name ||
            role?.title ||
            "";

          reset({
            roleName,
            permissionIds
          });
          setPermissionError(false);
        } else {
          // Reset form for new role
          reset({
            roleName: "",
            permissionIds: []
          });
          setPermissionError(false);
        }
      } catch (err) {
        console.error(err);
        toast.error(t('role_load_failed', "Failed to load role data"));
      } finally {
        setInitialLoading(false);
      }
    };

    loadData();
  }, [open, roleId, reset, t]);

  // Toggle single permission
  const togglePermission = (permId) => {
    const updated = selectedIds.includes(permId)
      ? selectedIds.filter(id => id !== permId)
      : [...selectedIds, permId];

    setValue(
      "permissionIds",
      updated
    );
    if (updated.length > 0) setPermissionError(false);
  };

  // Select/Deselect all permissions in a module
  const handleSelectAll = (module, checked) => {
    const moduleIds = groupedPermissions[module].map(p => p.id);
    let updated = [...selectedIds];

    if (checked) {
      moduleIds.forEach(id => {
        if (!updated.includes(id)) updated.push(id);
      });
    } else {
      updated = updated.filter(id => !moduleIds.includes(id));
    }

    setValue("permissionIds", updated);
    if (updated.length > 0) setPermissionError(false);
  };

  // Get module checkbox state (checked/indeterminate)
  const getModuleState = (module) => {
    const moduleIds = groupedPermissions[module].map(p => p.id);
    const count = moduleIds.filter(id => selectedIds.includes(id)).length;

    if (count === 0) return { checked: false, indeterminate: false };
    if (count === moduleIds.length)
      return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  };

  // Submit form
  const onSubmit = async (data) => {
    if (!data.permissionIds || data.permissionIds.length === 0) {
      setPermissionError(true);
      toast.error(t('select_at_least_one_permission', 'Please select at least one permission'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        roleName: data.roleName,
        permissionIds: data.permissionIds
      };

      if (roleId) {
        await axiosInstance.put(`/api/admin/roles/${roleId}`, payload);
        toast.success(t('role_updated', "Role updated successfully"));
      } else {
        await axiosInstance.post("/api/admin/roles", payload);
        toast.success(t('role_created', "Role created successfully"));
      }

      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(t('save_failed', "Save failed"));
    } finally {
      setLoading(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      reset();
      setPermissionError(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
        {roleId ? t('edit_role', 'Edit Role') : t('create_role', 'Create Role')}
        <IconButton onClick={handleClose} disabled={loading} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          borderColor: 'rgba(230, 210, 249, 0.1)',
          padding: '24px 16px',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(230, 210, 249, 0.3)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(230, 210, 249, 0.5)',
            },
          },
        }}
      >
        {initialLoading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress sx={{ color: '#FF69B4' }} />
          </Box>
        ) : (
          <form id="role-form" onSubmit={handleSubmit(onSubmit)}>
            <Grid container>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  {...register("roleName", {
                    required: t(
                      'role_name_required',
                      'Role name is required'
                    )
                  })}
                  error={!!errors.roleName}
                  helperText={errors.roleName?.message}
                  placeholder={t('role_name', 'Role Name')}
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-input': {
                      padding: '12px 20px',
                      fontWeight: '400',
                      color: '#fff',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255,255,255,0.6)',
                      opacity: 1,
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontSize: '16px', color: 'white', fontWeight: '400', mb: '5px' }}> {t('permissions', 'Permissions')} </Typography>
                <Box
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(230, 210, 249, 0.3)',
                      borderRadius: '4px',
                      '&:hover': {
                        background: 'rgba(230, 210, 249, 0.5)',
                      },
                    },
                  }}
                >
                  {Object.entries(groupedPermissions).map(([module, perms]) => {
                    const { checked, indeterminate } = getModuleState(module);

                    return (
                      <Accordion
                        key={module}
                        defaultExpanded
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(230, 210, 249, 0.1)',
                          borderRadius: '24px !important',
                          boxShadow: 'none',
                          mb: 1,
                          '&:before': {
                            display: 'none',
                          },
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: '#fff', }} />}
                          sx={{
                            minHeight: '44px !important',
                            pl: '20px',
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center',
                              marginBottom: '0',
                              marginTop: '0',
                            },
                          }}
                        >
                          <Typography sx={{ flexGrow: 1, textTransform: 'capitalize', fontSize: '14px', fontWeight: '400' }}>
                            {module}
                          </Typography>
                          <Checkbox
                            checked={checked}
                            indeterminate={indeterminate}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectAll(module, e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: 'rgba(255, 255, 255, 0.7)',
                              padding: '0',
                              mr: '6px',
                              '&.Mui-checked': {
                                color: '#FF69B4',
                              },
                              '&.MuiCheckbox-indeterminate': {
                                color: '#FF69B4',
                              },
                            }}
                          />
                        </AccordionSummary>

                        <AccordionDetails sx={{ pl: '26px', pr: '20px', pt: '7px' }}>
                          <Grid container spacing={1}>
                            {perms.map((perm) => (
                              <Grid item xs={12} sm={6} md={4} key={perm.id}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedIds.includes(perm.id)}
                                      onChange={() => togglePermission(perm.id)}
                                      sx={{
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        padding: '0',
                                        mr: '6px',
                                        '&.Mui-checked': {
                                          color: '#FF69B4',
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: '400', color: '#d9d9d9' }}>
                                      {perm.name}
                                    </Typography>
                                  }
                                  sx={{
                                    '& .MuiTypography-root': {
                                      fontSize: '14px',
                                    },
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
                {permissionError && !hasSelectedPermissions && (
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: '12px',
                      color: '#f44336'
                    }}
                  >
                    {t('select_at_least_one_permission', 'Select at least one permission')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
        <Button onClick={handleClose} disabled={loading} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>
          {t('cancel', 'Cancel')}
        </Button>
        <Button
          type="submit"
          form="role-form"
          variant="contained"
          onClick={() => {
            if (!hasSelectedPermissions) setPermissionError(true);
          }}
          disabled={loading || initialLoading}
          sx={{
            py: 1.5,
            borderRadius: '999px',
            fontWeight: '400',
            fontSize: '15px',
            lineHeight: '20px',
            textTransform: 'none',
            background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
            textTransform: 'capitalize',
            minWidth: { xs: '100%', sm: '150px' },
            marginLeft: '10px',
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          ) : (
            roleId ? t('update', 'Update') : t('create', 'Create')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleFormModal;
